/**
 * MobileCombatMenu Component
 *
 * A bottom sheet menu for mobile combat mode.
 * Features:
 * - Slide-up animation from bottom of screen
 * - Swipe-down to dismiss gesture
 * - Safe area inset support for notched devices
 * - Backdrop click to close
 *
 * @module components/combat/MobileCombatMenu
 */

import React, { useRef, useEffect } from 'react';
import styles from './MobileCombatMenu.module.css';

export interface MobileCombatMenuProps {
  /** Whether the menu is open */
  isOpen: boolean;
  /** Callback when menu should close */
  onClose: () => void;
  /** Callback when Leave Battle is pressed */
  onLeaveBattle: () => void;
}

/**
 * MobileCombatMenu displays a bottom sheet with combat options.
 *
 * @example
 * ```tsx
 * <MobileCombatMenu
 *   isOpen={menuOpen}
 *   onClose={() => setMenuOpen(false)}
 *   onLeaveBattle={handleLeaveBattle}
 * />
 * ```
 */
export function MobileCombatMenu({
  isOpen,
  onClose,
  onLeaveBattle,
}: MobileCombatMenuProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const startY = useRef<number>(0);
  const currentY = useRef<number>(0);

  // Reset transform when opening
  useEffect(() => {
    if (isOpen && sheetRef.current) {
      sheetRef.current.style.transform = '';
    }
  }, [isOpen]);

  // Handle touch start - record initial position
  const handleTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
    currentY.current = e.touches[0].clientY;
  };

  // Handle touch move - translate sheet based on drag distance
  const handleTouchMove = (e: React.TouchEvent) => {
    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;

    // Only allow dragging downward
    if (diff > 0 && sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${diff}px)`;
    }
  };

  // Handle touch end - dismiss if dragged far enough, otherwise snap back
  const handleTouchEnd = () => {
    const diff = currentY.current - startY.current;

    if (diff > 80) {
      // Dismiss threshold reached (slightly less than MobileHelpSheet for quicker dismiss)
      onClose();
    } else if (sheetRef.current) {
      // Snap back to open position
      sheetRef.current.style.transform = '';
    }

    // Reset tracking values
    startY.current = 0;
    currentY.current = 0;
  };

  // Handle backdrop click
  const handleBackdropClick = () => {
    onClose();
  };

  // Handle Leave Battle click
  const handleLeaveBattle = () => {
    onClose();
    onLeaveBattle();
  };

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className={`${styles.backdrop} ${isOpen ? styles.visible : ''}`}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        className={`${styles.sheet} ${isOpen ? styles.open : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="combat-menu-title"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag Handle */}
        <div className={styles.dragHandle} aria-hidden="true" />

        {/* Title */}
        <h2 id="combat-menu-title" className={styles.title}>
          Combat Menu
        </h2>

        {/* Menu Items */}
        <div className={styles.menuItems}>
          {/* Leave Battle Button */}
          <button
            type="button"
            className={styles.leaveButton}
            onClick={handleLeaveBattle}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M3 3a1 1 0 011-1h12a1 1 0 011 1v14a1 1 0 01-1 1H4a1 1 0 01-1-1V3zm10.293 6.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L10 10.586l1.293-1.293z"
                clipRule="evenodd"
              />
            </svg>
            Leave Battle
          </button>

          {/* Future: Sound Toggle would go here */}
        </div>

        {/* Close Button */}
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </>
  );
}

export default MobileCombatMenu;
